import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, notFound, useParams } from '@tanstack/react-router';

import { CategoryField } from '@/components/admin/category-field';
import { ProductEditor } from '@/components/admin/product-editor';
import { ColourShopControls } from '@/components/admin/colour-shop';
import { Badge, Callout, buttonClass, inputClass } from '@/components/ui';
import { formatCents } from '@/lib/money';
import { isRetired } from '@/lib/style-options';
import type { StylePage } from '@/lib/stock-types';
import { adminShellQuery, styleOptionsQuery, styleQuery } from '@/queries';

/**
 * One style, whole. Spec 010, tracker #10.
 *
 * Guy: "I feel I might want to get publishing and stock on hand in one place."
 * A style was three records on three screens. This page holds all of it: the
 * name and blank cost, a row per colour with its price and its place in the
 * shop, a grid of the stock by colour and size, and the words and photos.
 *
 * Every value still has one writer. The page calls the same routes the other
 * screens call, so a price changed here and a price changed on the stock item
 * panel are the same change (spec 014, FR-1420). Publishing only links here.
 */

async function call(path: string, init?: RequestInit) {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : res.json();
}

/** Dollars as typed, to whole cents. NaN stays NaN so the server can refuse it. */
const toCentsTyped = (dollars: string) => Math.round(Number(dollars) * 100);

const sectionHeading = 'mb-3 text-[15px] font-semibold';
const box = 'rounded-[10px] border border-line bg-panel';

/** One mutation shape for the page: send, then read the style again. */
function useAction(productId: string, onDone?: () => void) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: ({ path, method, body }: { path: string; method: string; body: unknown }) =>
      call(path, { method, body: JSON.stringify(body) }),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'style', productId] });
      onDone?.();
    },
    onError: (e: Error) => setError(e.message),
  });
  return { ...m, error };
}

function Header({ page, mayEdit }: { page: StylePage; mayEdit: boolean }) {
  const s = page.style;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name);
  const [category, setCategory] = useState(s.category);
  const [cost, setCost] = useState(s.base_price_cents == null ? '' : (s.base_price_cents / 100).toFixed(2));
  const [material, setMaterial] = useState(s.material ?? '');
  const [weight, setWeight] = useState(s.weight_gsm == null ? '' : String(s.weight_gsm));
  const [fit, setFit] = useState(s.fit ?? '');
  const [active, setActive] = useState(s.active);
  const save = useAction(s.id, () => setEditing(false));
  const facts = [s.material, s.weight_gsm != null ? `${s.weight_gsm} GSM` : null, s.fit].filter(Boolean);

  return (
    <div className="mb-6">
      <Link to="/admin/product-listing" className="text-[12.5px] text-muted hover:underline">
        Products
      </Link>
      <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold tracking-[-0.01em] [overflow-wrap:anywhere]">{s.name}</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            {s.category} &middot; {s.type}
            {s.base_price_cents != null && <> &middot; blank cost {formatCents(s.base_price_cents)}</>}
            {' '}&middot; {s.active ? 'active' : 'inactive'}
          </p>
          {facts.length > 0 && <p className="mt-0.5 text-[12.5px] text-muted">{facts.join(' · ')}</p>}
        </div>
        {mayEdit && !editing && (
          <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setEditing(true)}>
            Edit the product
          </button>
        )}
      </div>

      {editing && (
        <form
          className={`${box} mt-4 grid gap-3 p-4 sm:grid-cols-3`}
          aria-label="Edit the style"
          onSubmit={(e) => {
            e.preventDefault();
            const body: Record<string, unknown> = {
              name, category, material, fit, active,
              // As typed, so "240gsm" is refused by the server with a reason
              // rather than sent as NaN, which JSON turns into a silent null.
              weight_gsm: weight.trim() === '' ? null : weight.trim(),
            };
            if (s.base_price_cents != null) body.base_price_cents = toCentsTyped(cost);
            save.mutate({ path: `/api/products/${s.id}`, method: 'PATCH', body });
          }}
        >
          {save.error && <Callout tone="danger" title={save.error} className="sm:col-span-3" />}
          <label className="text-[11px] font-medium text-muted">
            Name
            <input className={`${inputClass} mt-1`} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="text-[11px] font-medium text-muted" htmlFor="style-category">
            Category
            {/*
              Tracker #12, twice. First: "is this a field that is linked to
              somewhere else or a Manual entry Only". It is yours, and the
              Shopify import no longer writes over it. Then: "Can you make it
              so we can add the category's ourselves". So the choices are the
              categories your own styles carry, and a new one is typed once.
            */}
            <div className="mt-1">
              <CategoryField id="style-category" value={category} onChange={setCategory} />
            </div>
            <span className="mt-1 block font-normal text-[11px] text-muted">
              Yours. Pick one, or add a new one and it joins the list. An import from
              Shopify will not change it.
            </span>
          </label>
          {s.base_price_cents != null && (
            <label className="text-[11px] font-medium text-muted">
              Blank cost, in dollars
              <input
                className={`${inputClass} mt-1`}
                inputMode="decimal"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
              />
            </label>
          )}
          {/* Spec 011, FR-1101 and FR-1102. Material, weight and fit are the
              product's, said once, and every stock item of it shares them. */}
          <label className="text-[11px] font-medium text-muted">
            Material
            <input
              className={`${inputClass} mt-1`} value={material} placeholder="For example 92% nylon, 8% spandex"
              onChange={(e) => setMaterial(e.target.value)}
            />
          </label>
          <label className="text-[11px] font-medium text-muted">
            Weight, in GSM
            <input
              className={`${inputClass} mt-1`} value={weight} inputMode="numeric" placeholder="For example 240"
              onChange={(e) => setWeight(e.target.value)}
            />
          </label>
          <label className="text-[11px] font-medium text-muted">
            Fit
            <input
              className={`${inputClass} mt-1`} value={fit} list="fit-choices" placeholder="For example Regular"
              onChange={(e) => setFit(e.target.value)}
            />
            <datalist id="fit-choices">
              {['Boxy', 'Regular', 'Slim', 'Oversized', 'Cropped', 'Relaxed'].map((f) => <option key={f} value={f} />)}
            </datalist>
          </label>
          <label className="flex items-center gap-2 text-[13px] sm:col-span-3">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active. An inactive product stays with its stock and history, and is not offered for new work.
          </label>
          <div className="flex gap-2 sm:col-span-3">
            <button type="submit" className={buttonClass('primary', 'sm')} disabled={save.isPending}>
              {save.isPending ? 'Saving' : 'Save'}
            </button>
            <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
          <p className="text-[11.5px] text-muted sm:col-span-3">
            A new name is carried to the stock items and the shop rows that carry the old one.
          </p>
        </form>
      )}
    </div>
  );
}

function ColourRow(
  { page, colour, may }:
  {
    page: StylePage;
    colour: { name: string; hex: string; retired?: boolean };
    may: { create: boolean; edit: boolean };
  },
) {
  const id = page.style.id;
  const qc = useQueryClient();
  const listing = page.listings.find((l) => l.color_name === colour.name);
  const variants = page.variants.filter((v) => v.colour === colour.name);
  // The list's retired mark (spec 014, FR-1450), so a colour with no variants
  // can be retired too.
  const retired = isRetired({ colors: page.colours, retired_sizes: page.retired_sizes }, 'colour', colour.name);
  const onHand = page.stock
    ?.filter((s) => variants.some((v) => v.id === s.variant_id))
    .reduce((n, s) => n + s.on_hand, 0);

  const [renaming, setRenaming] = useState(false);
  const act = useAction(id);

  return (
    <li className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 ${retired ? 'opacity-60' : ''}`}>
      <span
        aria-hidden="true"
        className="h-5 w-5 shrink-0 rounded-full border border-line"
        style={{ background: colour.hex }}
      />
      <span className="min-w-[120px] flex-1">
        <span className="block text-[13.5px] font-medium">{colour.name}</span>
        <span className="block text-[11.5px] text-muted">
          {onHand != null && <>{onHand} on hand &middot; </>}
          {retired
            ? 'Retired'
            : listing == null
              ? 'Not in the shop'
              : listing.active ? 'In the shop' : 'Out of the shop'}
        </span>
      </span>

      {/* The same control as the stock item panel (spec 014, FR-1420,
          FR-1423), so the price and the switch have one shape and one writer. */}
      {!retired && (
        <ColourShopControls
          productId={id}
          colour={colour.name}
          listing={listing ?? null}
          may={may}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'style', id] });
            void qc.invalidateQueries({ queryKey: ['stock-item'] });
          }}
        />
      )}

      {may.edit && (
        <span className="flex gap-1">
          <button type="button" className={buttonClass('tertiary', 'sm')} onClick={() => setRenaming(!renaming)}>
            Rename
          </button>
          <button
            type="button"
            className={buttonClass('tertiary', 'sm')}
            disabled={act.isPending}
            onClick={() => act.mutate({
              path: `/api/styles/${id}/variants`, method: 'PATCH', body: { colour: colour.name, active: retired },
            })}
          >
            {retired ? 'Restore' : 'Retire'}
          </button>
        </span>
      )}

      {renaming && (
        <RenameOption
          productId={id} kind="colour" from={colour.name} hex={colour.hex} onDone={() => setRenaming(false)}
        />
      )}

      {act.error && <p className="w-full text-[12px] text-[color:var(--color-danger-ink)]">{act.error}</p>}
    </li>
  );
}

function AddOne(
  { productId, kind }: { productId: string; kind: 'colour' | 'size' },
) {
  const [name, setName] = useState('');
  const [hex, setHex] = useState('#16191C');
  const act = useAction(productId, () => setName(''));
  const label = kind === 'colour' ? 'Add a colour' : 'Add sizes';
  // The names the catalogue already uses, so "Black" is not typed three ways.
  // Spec 011, FR-1114.
  const options = useQuery({ ...styleOptionsQuery, retry: false }).data;
  const listId = `${kind}-suggestions`;

  return (
    <form
      className="flex flex-wrap items-center gap-2 px-4 py-3"
      aria-label={label}
      onSubmit={(e) => {
        e.preventDefault();
        act.mutate(kind === 'colour'
          ? { path: `/api/styles/${productId}/colours`, method: 'POST', body: { name, hex } }
          : { path: `/api/styles/${productId}/sizes`, method: 'POST', body: { size: name } });
      }}
    >
      <input
        value={name}
        list={listId}
        onChange={(e) => {
          setName(e.target.value);
          const known = options?.colours.find((c) => c.name.toLowerCase() === e.target.value.trim().toLowerCase());
          if (kind === 'colour' && known) setHex(known.hex.toUpperCase());
        }}
        placeholder={kind === 'colour' ? 'Colour name, for example Sand' : 'Sizes, for example 6, 8, 10, 12 or XS, S, M'}
        aria-label={kind === 'colour' ? 'New colour name' : 'New sizes, with a comma between them'}
        className={`${inputClass} min-w-[180px] flex-1`}
      />
      <datalist id={listId}>
        {(kind === 'colour' ? options?.colours ?? [] : options?.sizes ?? []).map((o) => (
          <option key={o.name} value={o.name} />
        ))}
      </datalist>
      {kind === 'colour' && (
        <input
          type="color"
          value={hex}
          onChange={(e) => setHex(e.target.value.toUpperCase())}
          aria-label="New colour swatch"
          className="h-9 w-12 cursor-pointer rounded-[6px] border border-line bg-panel"
        />
      )}
      <button type="submit" className={buttonClass('secondary', 'sm')} disabled={act.isPending || !name.trim()}>
        {label}
      </button>
      {act.error && <p className="w-full text-[12px] text-[color:var(--color-danger-ink)]">{act.error}</p>}
    </form>
  );
}

/**
 * Rename a colour or a size (spec 011, FR-1110, FR-1111). The new name goes to
 * the list, the stock items, and the shop row of a colour, in one transaction.
 */
function RenameOption(
  { productId, kind, from, hex, onDone }:
  { productId: string; kind: 'colour' | 'size'; from: string; hex?: string; onDone: () => void },
) {
  const [to, setTo] = useState(from);
  const [swatch, setSwatch] = useState(hex ?? '#16191C');
  const act = useAction(productId, onDone);
  return (
    <form
      className="flex w-full flex-wrap items-center gap-2"
      aria-label={`Rename ${kind} ${from}`}
      onSubmit={(e) => {
        e.preventDefault();
        act.mutate({
          path: `/api/styles/${productId}/rename`, method: 'POST',
          body: { kind, from, to, ...(kind === 'colour' ? { hex: swatch } : {}) },
        });
      }}
    >
      <input
        value={to}
        onChange={(e) => setTo(e.target.value)}
        aria-label={`New name for ${from}`}
        className={`${inputClass} min-w-[160px] flex-1`}
      />
      {kind === 'colour' && (
        <input
          type="color"
          value={swatch}
          onChange={(e) => setSwatch(e.target.value.toUpperCase())}
          aria-label={`Swatch for ${from}`}
          className="h-9 w-12 cursor-pointer rounded-[6px] border border-line bg-panel"
        />
      )}
      <button type="submit" className={buttonClass('primary', 'sm')} disabled={act.isPending || !to.trim()}>
        {act.isPending ? 'Saving' : 'Save'}
      </button>
      <button type="button" className={buttonClass('secondary', 'sm')} onClick={onDone}>Cancel</button>
      <span className="w-full text-[11.5px] text-muted">
        Every stock item and order keeps its history. The descriptions the system wrote take the new name.
      </span>
      {act.error && <p className="w-full text-[12px] text-[color:var(--color-danger-ink)]">{act.error}</p>}
    </form>
  );
}

/**
 * The sizes, in their order (spec 011, FR-1111, FR-1112, FR-1115). Each can
 * move, be renamed, or be retired. There is no fixed size system: letters,
 * numbers and ages are all sizes.
 */
function SizeList({ page, mayEdit }: { page: StylePage; mayEdit: boolean }) {
  const id = page.style.id;
  const [renaming, setRenaming] = useState<string | null>(null);
  const act = useAction(id);
  const retired = (size: string) =>
    isRetired({ colors: page.colours, retired_sizes: page.retired_sizes }, 'size', size);
  const move = (from: number, by: number) => {
    const next = [...page.sizes];
    const [s] = next.splice(from, 1);
    next.splice(from + by, 0, s!);
    act.mutate({ path: `/api/styles/${id}/sizes`, method: 'PUT', body: { sizes: next } });
  };
  return (
    <ul className="divide-y divide-line-soft border-t border-line-soft">
      {page.sizes.map((size, i) => (
        <li key={size} className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 ${retired(size) ? 'opacity-60' : ''}`}>
          <span className="min-w-[80px] flex-1 font-[family-name:var(--font-mono)] text-[13px]">
            {size}{retired(size) && <span className="ml-2 font-sans text-[11.5px] text-muted">Retired</span>}
          </span>
          {mayEdit && (
            <span className="flex flex-wrap gap-1">
              <button
                type="button" className={buttonClass('tertiary', 'sm')} disabled={i === 0 || act.isPending}
                aria-label={`Move ${size} earlier`} onClick={() => move(i, -1)}
              >
                Earlier
              </button>
              <button
                type="button" className={buttonClass('tertiary', 'sm')}
                disabled={i === page.sizes.length - 1 || act.isPending}
                aria-label={`Move ${size} later`} onClick={() => move(i, 1)}
              >
                Later
              </button>
              <button
                type="button" className={buttonClass('tertiary', 'sm')}
                aria-label={`Rename size ${size}`} onClick={() => setRenaming(renaming === size ? null : size)}
              >
                Rename
              </button>
              <button
                type="button" className={buttonClass('tertiary', 'sm')} disabled={act.isPending}
                aria-label={`${retired(size) ? 'Restore' : 'Retire'} size ${size}`}
                onClick={() => act.mutate({
                  path: `/api/styles/${id}/variants`, method: 'PATCH', body: { size, active: retired(size) },
                })}
              >
                {retired(size) ? 'Restore' : 'Retire'}
              </button>
            </span>
          )}
          {renaming === size && (
            <RenameOption productId={id} kind="size" from={size} onDone={() => setRenaming(null)} />
          )}
        </li>
      ))}
      {act.error && <li className="px-4 py-2 text-[12px] text-[color:var(--color-danger-ink)]">{act.error}</li>}
    </ul>
  );
}

/**
 * The stock, a row per colour and a column per size. A cell opens that stock
 * item's panel on Stock on hand, which already holds the adjustment, the move
 * and the history, so none of that is built twice.
 */
function StockGrid({ page }: { page: StylePage }) {
  const cell = (colour: string, size: string) => {
    const v = page.variants.find((x) => x.colour === colour && x.size === size);
    const s = v ? page.stock?.find((x) => x.variant_id === v.id) : undefined;
    return { v, s };
  };
  const sizeRetired = (size: string) =>
    isRetired({ colors: page.colours, retired_sizes: page.retired_sizes }, 'size', size);

  return (
    <>
      {/* `relative`, so nothing positioned inside can widen the page on a
          phone (tracker #8). */}
      <div className="relative overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-line">
              <th scope="col" className="px-4 py-2 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-muted">
                Colour
              </th>
              {page.sizes.map((size) => (
                <th key={size} scope="col" className={`px-3 py-2 text-right ${sizeRetired(size) ? 'opacity-60' : ''}`}>
                  <span className="block font-[family-name:var(--font-mono)] text-[12px]">{size}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {page.colours.map((c) => (
              <tr key={c.name} className="border-b border-line-soft last:border-b-0">
                <th scope="row" className="px-4 py-2 text-left font-medium">
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true" className="h-3 w-3 shrink-0 rounded-full border border-line" style={{ background: c.hex }} />
                    {c.name}
                  </span>
                </th>
                {page.sizes.map((size) => {
                  const { v, s } = cell(c.name, size);
                  return (
                    <td key={size} className={`px-3 py-2 text-right font-[family-name:var(--font-mono)] ${v && !v.active ? 'opacity-60' : ''}`}>
                      {s ? (
                        <Link
                          to="/admin/stock"
                          search={{ open: s.item_id }}
                          className={`hover:underline ${s.on_hand === 0 ? 'text-muted' : 'font-semibold'}`}
                          title={s.places.map((p) => `${p.location}: ${p.on_hand}`).join(', ') || 'Not on a shelf'}
                          aria-label={`${c.name} ${size}: ${s.on_hand} on hand`}
                        >
                          {s.on_hand}
                        </Link>
                      ) : (
                        <span className="text-faint" title="No stock item">&ndash;</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function AdminStylePage() {
  const { productId } = useParams({ strict: false }) as { productId: string };
  const page = useSuspenseQuery(styleQuery(productId)).data;
  const qc = useQueryClient();
  const { can } = useSuspenseQuery(adminShellQuery).data;
  if (!page) throw notFound();
  const may = {
    create: (can.catalogue ?? []).includes('create'),
    edit: (can.catalogue ?? []).includes('edit'),
  };
  const inShop = page.listings.filter((l) => l.active).length;

  return (
    <div>
      <Header
        key={[page.style.name, page.style.base_price_cents, page.style.material, page.style.weight_gsm,
          page.style.fit, page.style.active].join('|')}
        page={page}
        mayEdit={may.edit}
      />

      <section aria-labelledby="colours-heading" className="mb-8">
        <h2 id="colours-heading" className={sectionHeading}>
          Colours <Badge tone="neutral">{inShop} of {page.colours.length} in the shop</Badge>
        </h2>
        <ul className={`${box} divide-y divide-line-soft`}>
          {page.colours.map((c) => (
            <ColourRow
              key={`${c.name}-${page.listings.find((l) => l.color_name === c.name)?.retail_price_cents ?? 'none'}`}
              page={page}
              colour={c}
              may={may}
            />
          ))}
          {may.create && <li><AddOne productId={productId} kind="colour" /></li>}
        </ul>
        <p className="mt-2 text-[11.5px] text-muted">
          The price is for every size of a colour. A price changed here is not sent to Shopify.
        </p>
      </section>

      {page.stock != null && (
        <section aria-labelledby="stock-heading" className="mb-8">
          <h2 id="stock-heading" className={sectionHeading}>Stock by size</h2>
          <div className={box}>
            <StockGrid page={page} />
          </div>
          <p className="mt-2 text-[11.5px] text-muted">
            Click a number to adjust, move or count that size. Stock arrives on a purchase order.
          </p>
        </section>
      )}

      <section aria-labelledby="sizes-heading" className="mb-8">
        <h2 id="sizes-heading" className={sectionHeading}>Sizes, in order</h2>
        <div className={box}>
          <SizeList page={page} mayEdit={may.edit} />
          {may.create && <div className="border-t border-line-soft"><AddOne productId={productId} kind="size" /></div>}
        </div>
        <p className="mt-2 text-[11.5px] text-muted">
          A retired size gets no new stock items. Its stock and history stay.
        </p>
      </section>

      <section aria-labelledby="words-heading">
        <h2 id="words-heading" className={sectionHeading}>Description and photos</h2>
        <div className={box}>
          <ProductEditor
            product={page.style}
            onSaved={() => { void qc.invalidateQueries({ queryKey: ['admin', 'style', productId] }); }}
          />
        </div>
      </section>
    </div>
  );
}
